import { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { FUND, LSE, XDJI, XNAS } from 'src/Api';
import { AddInvestmentModal } from './index';
import Axios from 'axios';
import YouInvest, { CONFIGURED_SERVICE_WATCHLISTS } from 'src/Api/YouInvest';
import { Loader } from '../../Molecules/Loader';
import { isStreamingEnabledForInstrument, isBeta } from 'src/Utils';

export default function MarketHoursDelayInfo({ quote, marketOpen, streamingEnabled, streamingSwitcherEvent, settings, isGuest, details }) {

    const [hours, setHours] = useState(0);
    const [minutes, setMinutes] = useState(0);
    const [date, setDate] = useState(null);
    const [marketOpenResponse, setMarketOpenResponse] = useState(false);
    const [watchlists, setWatchlists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [watchlistsEnabled] = useState(YouInvest.getConfiguredServiceAllowed(CONFIGURED_SERVICE_WATCHLISTS, settings) || isBeta());
    const [reload, setReload] = useState(false);

    useEffect(() => {
        if (quote.__typename !== FUND) {
            const currentDate = new Date();

            if (marketOpen && !(currentDate.getDay() === 0 || currentDate.getDay() === 6)) {
                let openTime = marketOpen.opensAt;
                let closeTime = marketOpen.closesAt;
                openTime = openTime.split(" ")[0];
                closeTime = closeTime.split(" ")[0];

                const currentTime = moment.utc(new Date);
                const startTime = moment.utc(openTime, "HH:mm"); // opening time of the market in hours and minutes
                const endTime = moment.utc(closeTime, "HH:mm"); // closing time of the market in hours and minutes
                const durationToStart = moment.duration(startTime.diff(currentTime)); // the amount of time before the market opens
                const durationToEnd = moment.duration(endTime.diff(currentTime)); // the amount of time before the market closes

                if ((durationToStart.hours() > 0 || durationToStart.minutes() > 0) || (durationToEnd.hours() <= 0 && durationToEnd.minutes() <= 0)) {
                    marketOpen.isOpen = false;
                } else {
                    marketOpen.isOpen = true;
                }

                setMarketOpenResponse(marketOpen);
                setHours(durationToEnd.hours());
                setMinutes(durationToEnd.minutes());
            }
            const date = quote.bidPriceDate;
            const time = quote.bidPriceTime;
            if (date && time) {
                const formattedDate = moment(date, "DD-MM-YYYY").format("YYYY-MM-DD");
                const dataAndTime = moment.tz(`${formattedDate} ${time}`, "America/New_York").tz("Europe/London");
                setDate(dataAndTime.format("DD MMM YYYY, HH.mm z"));
            } else if (date) {
                setDate(moment(date, "DD-MM-YYYY").format("DD MMM YYYY"));
            } else if (quote.lastPriceDate) { // XMAD:SAN
                setDate(moment(quote.lastPriceDate, "DD-MM-YYYY").format("DD MMM YYYY"));
            } else {
                setDate(null);
            }
        } else {
            setDate(quote.lastPriceDate ? moment(quote.lastPriceDate, "YYYY-MM-DD").format("DD MMM YYYY") : null);
        }
    }, []);

    useEffect(() => {
        if (watchlistsEnabled) {
            setLoading(true);

            Axios({
                method: 'GET',
                url: `/api/v2/watchlists/index/get-watchlists`,
            }).then(response => {
                setLoading(false);
                setWatchlists(response.data);
            }).catch(error => {
                setLoading(false);
            });
        }
    }, [reload]);

    const renderButtons = () => {

        if (isGuest || isGuest === null) {
            return null;
        }

        return <>
            {(isStreamingEnabledForInstrument(quote) && !details.electiveProfessional) ? (<div data-testid="overviewStreaming" className="switch inline-block margin-top-0-sm margin-bottom-10 margin-right-10 text-align-center" style={{ border: '2px solid #373741', borderRadius: '50em', padding: '0.15em 1em' }}>
                <span className="h7 font-weight-bold black-text tooltipped" data-balloon-length="large" data-balloon="Allows access to live streaming for UK-listed shares. You can turn off live pricing at any time." data-balloon-pos="up">Live UK pricing:</span>
                <label className="h7 grey-500-text font-weight-normal inline-block margin-bottom-0-sm margin-left-5 margin-right-10 margin-left-10-sm">
                    Off<input type="checkbox" checked={streamingEnabled} onChange={streamingSwitcherEvent} /><span className="lever"></span>On
                </label>
            </div>) : null}

            {watchlists && watchlistsEnabled ? <AddInvestmentModal quote={quote} watchlists={watchlists} reload={reload} setReload={setReload} isGuest={isGuest} /> : null}
        </>
    }

    return (
        <div className="block highlight padding-bottom-10 padding-10-sm margin-top-neg-20 margin-left-neg-20 margin-right-neg-20 margin-top-0-sm margin-left-0-sm margin-right-0-sm">
            <p className="h7 block margin-bottom-10 max-characters-none">

                {(quote.__typename === FUND) ? <>
                    {date ? <span className="inline-block" data-testid="priceUpdatedDateText">Prices updated on {date}</span> : null}
                </> : <>
                    {
                        marketOpenResponse ? (
                            marketOpenResponse.isOpen ? (
                                <>
                                    <span className="font-weight-bold green-text" data-testid="marketStatusOpenText">Market open</span>
                                    &nbsp;Closes in <span className="font-weight-bold" data-testid="marketTimeText">{hours} hr {minutes} min</span>
                                </>
                            ) : (
                                <span className="font-weight-bold red-text" data-testid="marketStatusClosedText">Market closed</span>
                            )
                        ) : null
                    }
                    <br />
                    {quote.exchangeCode === LSE ? <span className="inline-block" data-testid="priceDelayedText">Prices delayed by at least 15 minutes</span> : null}
                    {[XDJI, XNAS].includes(quote.exchangeCode) ? <span className="inline-block" data-testid="priceDelayedText">Prices updated on the previous day&apos;s close</span> : null}
                    {[LSE, XDJI, XNAS].includes(quote.exchangeCode) && date ? <>&nbsp;|&nbsp;</> : null}
                    {date ? <span className="inline-block" data-testid="priceUpdatedDateTimeText">Prices updated on <span id="priceLastUpdatedTime">{date}</span></span> : null}
                </>}

                {quote.__typename === FUND && !date ? null : ' | '}
                <a className="inline-block text-underline-none" href="" onClick={(e) => {
                    e.preventDefault();
                    window.print();
                }} data-testid="marketPrintButton"><i className="fas fa-print" /> Print</a>

            </p>

            {loading ? <Loader /> : null}

            {renderButtons()}
        </div>
    );

}